package ui.app.assign.detail.wheel.editor.pages

import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import apps.App
import ssoft.frontend.generated.resources.*
import tools.rememberString
import ui.app.assign.detail.definition.TimersSettings
import ui.app.assign.detail.wheel.AppWheelRendererStartup
import ui.app.assign.detail.wheel.editor.AppEditorWheelScope
import ui.app.assign.detail.wheel.editor.widgets.FieldCustomImage
import ui.app.assign.detail.wheel.editor.widgets.FieldSlider
import ui.app.assign.detail.wheel.editor.widgets.FieldSwitch
import ui.app.assign.detail.wheel.editor.widgets.MobileDualView

private val singleColumn by lazy {
    listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardActivation(it) },
        { TimersSettings(it) },
        { CardQrCodeSettings(it) },
        { CardBackground(it) }
    )
}

private val doubleColumn by lazy {
    val left = listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardBackground(it) },
    )

    val right = listOf<@Composable AppEditorWheelScope.(AppEditorWheelScope.Wrapper) -> Unit>(
        { CardActivation(it) },
        { TimersSettings(it) },
        { CardQrCodeSettings(it) }
    )
    left to right
}

@Composable
fun AppEditorWheelScope.AppWheelPage04_Qr(nnvl: AppEditorWheelScope.Wrapper) {
    if (display.isMobileOrSmall) MobileView04(nnvl)
    else DesktopView04(nnvl)
}

@Composable
private fun AppEditorWheelScope.MobileView04(nnvl: AppEditorWheelScope.Wrapper) {
    MobileDualView(
        labelPrimary = Res.string.layer_bottom_wheel_tab_edit,
        labelSecondary = Res.string.layer_bottom_wheel_tab_preview,
        bodyPrimary = { ContentQr(nnvl, Modifier.fillMaxSize()) },
        bodySecondary = { PreviewQr(nnvl, Modifier.fillMaxSize()) }
    )
}

@Composable
private fun AppEditorWheelScope.DesktopView04(nnvl: AppEditorWheelScope.Wrapper) {
    Row(modifier = Modifier.fillMaxSize()) {
        PreviewQr(nnvl, Modifier.weight(1F))
        ContentQr(nnvl, Modifier.weight(1.5F))

    }
}

@Composable
private fun AppEditorWheelScope.ContentQr(nnvl: AppEditorWheelScope.Wrapper, weight: Modifier) {
    //
    val state = rememberLazyListState()
    Box(weight.fillMaxHeight()) {
        LazyColumn(
            state = state,
            modifier = Modifier.fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(
                top = 16.dp,
                start = 16.dp,
                end = 16.dp,
                bottom = 64.dp
            )
        ) {
            if (display.isMobileOrSmall || display.resolution.width < 1300F) {
                item {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        singleColumn.forEach { layout ->
                            layout(nnvl)
                        }
                        Next(nnvl)
                    }
                }
            } else {
                item {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Column(
                                modifier = Modifier.weight(1F),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                doubleColumn.first.forEach { layout ->
                                    layout(nnvl)
                                }
                            }
                            Column(
                                modifier = Modifier.weight(1F),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                doubleColumn.second.forEach { layout ->
                                    layout(nnvl)
                                }
                            }
                        }
                        Box(Modifier.fillMaxWidth()) {
                            ButtonNext(
                                modifier = Modifier.align(Alignment.Center),
                                validate = { true })
                        }
                    }
                }
            }
        }
        VerticalScrollbar(
            modifier = Modifier.fillMaxHeight()
                .align(Alignment.CenterEnd)
                .padding(4.dp),
            adapter = rememberScrollbarAdapter(state)
        )
    }
}

@Composable
private fun AppEditorWheelScope.Next(nnvl: AppEditorWheelScope.Wrapper) {
    Box(Modifier.fillMaxWidth()) {
        ButtonNext(Modifier.align(Alignment.Center)) {
            if (!nnvl.activateByQr.state && !nnvl.activateByTouch.state) {
                showMessage(Res.string.layer_bottom_wheel_validation_activation)
            } else true
        }
    }
}


@Composable
private fun AppEditorWheelScope.CardActivation(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_activation_control.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            FieldSwitch(
                label = Res.string.layer_bottom_wheel_page_activation_control_by_qr,
                desc = Res.string.layer_bottom_wheel_page_activation_control_by_qr_desc,
                field = nnvl.activateByQr,
                modifier = Modifier.fillMaxWidth()
            )
            FieldSwitch(
                label = Res.string.layer_bottom_wheel_page_activation_control_by_touch,
                desc = Res.string.layer_bottom_wheel_page_activation_control_by_touch_desc,
                field = nnvl.activateByTouch,
                modifier = Modifier.fillMaxWidth()
            )

        }
    }
}

@Composable
private fun AppEditorWheelScope.CardQrCodeSettings(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_activation_settings.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_activation_settings_scale,
                field = nnvl.qrScale,
                canEdit = nnvl.activateByQr.state,
                modifier = Modifier.fillMaxWidth()
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_activation_settings_padding,
                field = nnvl.qrPadding,
                canEdit = nnvl.activateByQr.state,
                modifier = Modifier.fillMaxWidth()
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_activation_settings_pos_x,
                field = nnvl.qrPosX,
                canEdit = nnvl.activateByQr.state,
                modifier = Modifier.fillMaxWidth()
            )

            FieldSlider(
                label = Res.string.layer_bottom_wheel_page_activation_settings_pos_y,
                field = nnvl.qrPosY,
                canEdit = nnvl.activateByQr.state,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.CardBackground(nnvl: AppEditorWheelScope.Wrapper) {
    Card(
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.align(Alignment.CenterHorizontally)
                .padding(16.dp)
        ) {
            Text(
                text = Res.string.layer_bottom_wheel_page_activation_bg.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.fillMaxWidth(),
                textAlign = TextAlign.Center
            )
            FieldCustomImage(
                companyId = args.companyId,
                entryId = args.appId,
                canEdit = nnvl.canEdit,
                image = nnvl.themeBgStart,
                label = Res.string.layer_bottom_wheel_page_activation_bg_start,
                orientation = when (nnvl.orientation.state) {
                    App.Wheel.Theme.ORIENTATION_LAND -> ResourceOrientation.HORIZONTAL
                    App.Wheel.Theme.ORIENTATION_PORT -> ResourceOrientation.VERTICAL
                    else -> ResourceOrientation.UNDEFINED
                },
                size = 384.dp,
                fixWidth = true,
                modifier = Modifier.align(Alignment.CenterHorizontally)
            )
        }
    }
}

@Composable
fun AppEditorWheelScope.TimersSettings(wrapper: AppEditorWheelScope.Wrapper) {
    TimersSettings(
        timerSplash = wrapper.timerSplash,
        timerGame = wrapper.timerGame,
        timerResult = wrapper.timerResult
    )
}

@Composable
private fun AppEditorWheelScope.PreviewQr(nnvl: AppEditorWheelScope.Wrapper, modifier: Modifier) {
    Box(
        modifier.fillMaxHeight()
            .background(
                color = MaterialTheme.colorScheme.surfaceContainer,
                shape = RoundedCornerShape(
                    topEnd = 8.dp,
                    bottomEnd = 8.dp
                )
            )
    ) {
        AppWheelRendererStartup(nnvl, Modifier.align(Alignment.Center))
    }
}